/**
 * Outil « Tangente glissante » (Boîte à outils, 1re → Tle).
 *
 * Un point A glisse sur la courbe de f : sa tangente le suit, avec le petit
 * triangle « 1 vers la droite, f′(a) vers le haut » qui fait lire la pente.
 * La sécante (AB), B d'abscisse a + h, devient la tangente quand h tend vers
 * 0 : le taux d'accroissement s'approche de f′(a). Sous la figure, la courbe
 * de f′ se trace point par point là où A est passé. Défi : lire f′(a) sur
 * une tangente tracée. Logique : `derivative.ts`.
 *
 * Clavier sur A : ← → d'une demi-unité (sur la grille des demis), ↑ ↓ d'un
 * dixième, Début / Fin aux bornes ; la valeur interdite de 1/x est sautée.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { clamp, makeFrame, snap, type Frame } from '../../ui/frame';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { DragHandle, FIGURE_BG_CLASS, HaloText, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  FUNCTIONS,
  H_STEPS,
  READ_HINT,
  fnById,
  hOk,
  hSide,
  makeReadQuiz,
  rate,
  ratText,
  slopeAt,
  tangentNodes,
  tangentText,
  trend,
  valueAt,
  type Fn,
  type FnId,
  type ReadQuiz,
} from './derivative';

const SIZE = 320;
const CURVE = 'var(--color-primary-500)';
const TANGENT = 'var(--color-ink-900)';
const SECANT = 'var(--color-accent-600)';
const UP = 'var(--color-success-600)';
const DOWN = 'var(--color-error-600)';
const LABEL = 'var(--color-ink-900)';
const ROUNDS = 5;

/** noms lus par le lecteur d'écran */
const SPOKEN: Record<FnId, string> = {
  square: 'x au carré',
  cubic: 'x au cube moins 3 x',
  inverse: 'inverse de x',
  sqrt: 'racine carrée de x',
  exp: 'exponentielle',
  ln: 'logarithme népérien',
};

const n = (v: number, d = 3) => formatNumber(v, d).replace('-', '−');
const xText = (a: number) => ratText(a) ?? n(a);
/** valeur : « = 2 » si exacte et courte, « ≈ 0,4495 » sinon */
const eq = (v: number, d = 4) => {
  const r = ratText(v);
  return r && r.length <= 8 ? `= ${r}` : `≈ ${n(v, d)}`;
};

function curvePath(fn: Fn, frame: Frame): string {
  const { vp } = frame;
  const N = 400;
  let d = '';
  let pen = false;
  for (let i = 0; i <= N; i++) {
    const x = vp.xMin + ((vp.xMax - vp.xMin) * i) / N;
    const y = fn.f(x);
    if (
      !Number.isFinite(y) ||
      y < vp.yMin - 3 ||
      y > vp.yMax + 3 ||
      (fn.id === 'inverse' && Math.abs(x) < 0.05)
    ) {
      pen = false;
      continue;
    }
    d += `${pen ? 'L' : 'M'}${frame.tx(x).toFixed(1)},${frame.ty(y).toFixed(1)} `;
    pen = true;
  }
  return d;
}

/** une droite de pente m par (x0 ; y0), sur toute la largeur de la fenêtre */
function lineAcross(frame: Frame, x0: number, y0: number, m: number) {
  const { vp, tx, ty } = frame;
  return {
    x1: tx(vp.xMin),
    y1: ty(y0 + m * (vp.xMin - x0)),
    x2: tx(vp.xMax),
    y2: ty(y0 + m * (vp.xMax - x0)),
  };
}

/** le triangle de pente : 1 vers la droite (ou la gauche au bord), f′(a) en hauteur */
function SlopeTriangle({ frame, fn, a }: { frame: Frame; fn: Fn; a: number }) {
  const { tx, ty, vp } = frame;
  const fa = fn.f(a);
  const m = fn.d(a);
  // vers la droite si le triangle y tient en entier, sinon vers la gauche
  const fits = (dir: number) => {
    const bx = a + dir;
    const by = fa + dir * m;
    return bx >= vp.xMin + 0.1 && bx <= vp.xMax - 0.1 && by >= vp.yMin && by <= vp.yMax;
  };
  const dir = fits(1) || !fits(-1) ? 1 : -1;
  const bx = a + dir;
  const by = fa + dir * m;
  const col = trend(fn, a) === 'up' ? UP : trend(fn, a) === 'down' ? DOWN : TANGENT;
  const text = dir === 1 ? `f′(a) = ${slopeAt(fn, a).text}` : '−f′(a)';
  // étiquette à droite du côté vertical si elle tient (≈ 7,6 px par caractère), sinon à gauche
  const labelRight = tx(bx) + 6 + text.length * 7.6 < frame.size - 4;
  return (
    <g data-testid="tg-triangle">
      <line
        x1={tx(a)}
        y1={ty(fa)}
        x2={tx(bx)}
        y2={ty(fa)}
        stroke={col}
        strokeWidth={2}
        strokeDasharray="5 4"
      />
      <line x1={tx(bx)} y1={ty(fa)} x2={tx(bx)} y2={ty(by)} stroke={col} strokeWidth={2.5} />
      <HaloText
        x={(tx(a) + tx(bx)) / 2}
        y={clamp(ty(fa) + (m >= 0 ? 16 : -8), 14, frame.size - 6)}
        textAnchor="middle"
        fontSize={13}
        fontWeight={700}
        fill={LABEL}
      >
        {dir === 1 ? '1' : '−1'}
      </HaloText>
      <HaloText
        x={labelRight ? tx(bx) + 6 : tx(bx) - 6}
        y={clamp((ty(fa) + ty(by)) / 2 + 4, 14, frame.size - 6)}
        textAnchor={labelRight ? 'start' : 'end'}
        fontSize={13}
        fontWeight={700}
        fill={LABEL}
      >
        {text}
      </HaloText>
    </g>
  );
}

/** courbe de f′ : les points (a ; f′(a)) déjà visités, ou toute la courbe */
function DerivPlot({ fn, visited, full }: { fn: Fn; visited: number[]; full: boolean }) {
  const W = 320;
  const H = 150;
  const { xMin, xMax } = fn.window;
  const ys: number[] = [];
  for (let i = 0; i <= 200; i++) {
    const x = fn.aMin + ((fn.aMax - fn.aMin) * i) / 200;
    if (fn.ok(x)) ys.push(fn.d(x));
  }
  const yMin = Math.floor(Math.max(-6, Math.min(-1, ...ys)));
  const yMax = Math.ceil(Math.min(6, Math.max(1, ...ys)));
  const tx = (x: number) => ((x - xMin) / (xMax - xMin)) * W;
  const ty = (y: number) => 8 + ((yMax - y) / (yMax - yMin)) * (H - 16);
  let path = '';
  if (full) {
    let pen = false;
    for (let i = 0; i <= 300; i++) {
      const x = fn.aMin + ((fn.aMax - fn.aMin) * i) / 300;
      const y = fn.ok(x) ? fn.d(x) : NaN;
      if (!Number.isFinite(y) || y < yMin - 1 || y > yMax + 1) {
        pen = false;
        continue;
      }
      path += `${pen ? 'L' : 'M'}${tx(x).toFixed(1)},${ty(y).toFixed(1)} `;
      pen = true;
    }
  }
  const drawn = visited.filter((a) => fn.d(a) >= yMin && fn.d(a) <= yMax);
  const yStep = yMax - yMin > 8 ? 2 : 1;
  const yTicks: number[] = [];
  for (let y = Math.ceil(yMin / yStep) * yStep; y <= yMax; y += yStep) yTicks.push(y);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-100 mx-auto block w-full max-w-[26rem] rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`Courbe de f′ : ${drawn.length} point${drawn.length > 1 ? 's' : ''} tracé${drawn.length > 1 ? 's' : ''}.`}
      data-testid="tg-deriv"
    >
      {Array.from(
        { length: Math.floor(xMax) - Math.ceil(xMin) + 1 },
        (_, i) => Math.ceil(xMin) + i,
      ).map((x) => (
        <line key={`x${x}`} x1={tx(x)} x2={tx(x)} y1={0} y2={H} stroke="var(--color-ink-100)" />
      ))}
      {yTicks.map((y) => (
        <g key={`y${y}`}>
          <line x1={0} x2={W} y1={ty(y)} y2={ty(y)} stroke="var(--color-ink-100)" />
          {y !== 0 && (
            <text
              x={W - 4}
              y={ty(y) - 2}
              textAnchor="end"
              fontSize={12}
              fill="var(--color-ink-500)"
            >
              {n(y, 0)}
            </text>
          )}
        </g>
      ))}
      <line x1={0} x2={W} y1={ty(0)} y2={ty(0)} stroke="var(--color-ink-400)" strokeWidth={1.25} />
      {xMin <= 0 && xMax >= 0 && (
        <line
          x1={tx(0)}
          x2={tx(0)}
          y1={0}
          y2={H}
          stroke="var(--color-ink-400)"
          strokeWidth={1.25}
        />
      )}
      {path && <path d={path} fill="none" stroke={SECANT} strokeWidth={2} opacity={0.6} />}
      {drawn.map((a) => {
        const y = fn.d(a);
        return (
          <circle
            key={a}
            cx={tx(a)}
            cy={ty(y)}
            r={3}
            fill={y > 1e-9 ? UP : y < -1e-9 ? DOWN : TANGENT}
          />
        );
      })}
      <text x={6} y={16} fontSize={12} fill="var(--color-ink-600)">
        f′
      </text>
    </svg>
  );
}

/** abscisse permise la plus proche (1/x : on saute 0 en restant du côté visé) */
function allowed(fn: Fn, v: number, from: number): number | null {
  const x = clamp(v, fn.aMin, fn.aMax);
  if (fn.ok(x)) return x;
  if (fn.id === 'inverse') {
    const y = (Math.sign(x) || Math.sign(from)) * 0.3;
    return fn.ok(y) ? y : null;
  }
  return fn.ok(fn.aMin) ? fn.aMin : null;
}

function Explore({ rank }: { rank: number }) {
  const list = FUNCTIONS.filter((f) => f.minRank <= rank);
  const [id, setId] = useState<FnId>('square');
  const fn = fnById(id);
  const frame = makeFrame(fn.window, SIZE);
  const { tx, ty, vp } = frame;
  const [a, setA] = useState(fn.aStart);
  const [secant, setSecant] = useState(false);
  const [hi, setHi] = useState(0);
  const [visited, setVisited] = useState<number[]>([fn.aStart]);
  const [full, setFull] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const go = (v: number | null) => {
    if (v === null) return;
    const x = Number(v.toFixed(10));
    setA(x);
    setVisited((s) => (s.includes(x) ? s : [...s, x]));
  };
  const drag = useDrag<'A'>({
    svgRef,
    frame,
    ids: ['A'],
    pointOf: () => ({ x: a, y: fn.f(a) }),
    onDrag: (_, p) => go(allowed(fn, snap(p.x, 0.1), a)),
    onBackground: (p) => go(allowed(fn, snap(p.x, 0.1), a)),
  });
  /** ← → : demi-unités sur la grille des demis ; ↑ ↓ : dixièmes ; Début / Fin : bornes */
  const keyProps = {
    tabIndex: 0,
    onKeyDown: (e: KeyboardEvent<SVGGElement>) => {
      let target: number | null = null;
      let step = 0;
      if (e.key === 'ArrowRight') {
        target = Math.floor(a / 0.5 + 1e-9) * 0.5 + 0.5;
        step = 0.5;
      } else if (e.key === 'ArrowLeft') {
        target = Math.ceil(a / 0.5 - 1e-9) * 0.5 - 0.5;
        step = -0.5;
      } else if (e.key === 'ArrowUp') {
        target = a + 0.1;
        step = 0.1;
      } else if (e.key === 'ArrowDown') {
        target = a - 0.1;
        step = -0.1;
      } else if (e.key === 'Home') target = fn.aMin;
      else if (e.key === 'End') target = fn.aMax;
      if (target === null) return;
      e.preventDefault();
      // valeur interdite : on continue dans le même sens
      let x = clamp(target, fn.aMin, fn.aMax);
      for (let k = 0; k < 12 && !fn.ok(x) && step !== 0; k++) x = clamp(x + step, fn.aMin, fn.aMax);
      go(allowed(fn, x, a));
    },
  };

  const choose = (next: FnId) => {
    const f = fnById(next);
    setId(next);
    setA(f.aStart);
    setVisited([f.aStart]);
    setFull(false);
  };

  const fa = fn.f(a);
  const m = fn.d(a);
  const tan = lineAcross(frame, a, fa, m);
  const side = hSide(fn, a);
  const h = H_STEPS[hi]! * side;
  const hUsable = hOk(fn, a, h) ? h : null;
  const b = hUsable === null ? null : a + hUsable;
  const t = trend(fn, a);
  const sec = b === null ? null : lineAcross(frame, a, fa, rate(fn, a, hUsable!));
  const fb = b === null ? NaN : fn.f(b);
  const bVisible = b !== null && b >= vp.xMin && b <= vp.xMax && fb >= vp.yMin && fb <= vp.yMax;
  const slope = slopeAt(fn, a);
  const val = valueAt(fn, a);

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Fonction" className="flex flex-wrap gap-1.5">
        {list.map((f) => (
          <ToggleChip key={f.id} on={f.id === id} onClick={() => choose(f.id)}>
            <span aria-hidden>{f.label}</span>
            <span className="sr-only">{SPOKEN[f.id]}</span>
          </ToggleChip>
        ))}
      </div>
      <p className="text-ink-700 text-sm">
        {frenchSpaces(`f(x) = ${fn.text} ; f′(x) = ${fn.derivText}. Fais glisser A sur la courbe.`)}
      </p>
      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Courbe de f(x) = ${fn.text} et sa tangente en A.`}
          data-testid="tg-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} />
          <path d={curvePath(fn, frame)} fill="none" stroke={CURVE} strokeWidth={3} />
          {secant && sec && (
            <g>
              <line {...sec} stroke={SECANT} strokeWidth={2} strokeDasharray="7 5" />
              {bVisible && (
                <g>
                  <circle cx={tx(b!)} cy={ty(fb)} r={5} fill={SECANT} />
                  <HaloText
                    x={tx(b!) > SIZE - 24 ? tx(b!) - 10 : tx(b!) + 8}
                    y={ty(fb) < 20 ? ty(fb) + 18 : ty(fb) - 8}
                    textAnchor={tx(b!) > SIZE - 24 ? 'end' : 'start'}
                    fontSize={14}
                    fontWeight={700}
                    fill={LABEL}
                  >
                    B
                  </HaloText>
                </g>
              )}
            </g>
          )}
          <line {...tan} stroke={TANGENT} strokeWidth={2.2} />
          <SlopeTriangle frame={frame} fn={fn} a={a} />
          <DragHandle
            x={tx(a)}
            y={ty(fa)}
            color={CURVE}
            label="A"
            labelDx={-14}
            labelDy={-14}
            name={`Point A d’abscisse ${xText(a)} (gauche et droite : une demi-unité ; haut et bas : un dixième)`}
            active={drag.active === 'A'}
            keyProps={keyProps}
          />
        </svg>
      </div>
      <p className="text-ink-900 text-sm" aria-live="polite" data-testid="tg-readout">
        {frenchSpaces(
          `A(${xText(a)} ; ${val.exact ? val.text : `≈ ${val.text}`}) : f′(${xText(a)}) ${slope.exact ? `= ${slope.text}` : `≈ ${slope.text}`}. Tangente : `,
        )}
        <span className="whitespace-nowrap">{frenchSpaces(tangentText(fn, a))}</span>.
      </p>
      <p className="text-ink-700 text-sm">
        {frenchSpaces(
          t === 'up'
            ? 'f′(a) > 0 : la tangente monte, f est croissante autour de a.'
            : t === 'down'
              ? 'f′(a) < 0 : la tangente descend, f est décroissante autour de a.'
              : 'f′(a) = 0 : tangente horizontale (un sommet ou un palier).',
        )}
      </p>

      <div className="space-y-2">
        <ToggleChip on={secant} onClick={() => setSecant(!secant)}>
          Sécante (AB)
        </ToggleChip>
        {secant && (
          <>
            <Segmented
              ariaLabel="h, écart entre A et B"
              value={String(hi)}
              options={H_STEPS.map((v, i) => ({ value: String(i), label: n(v) }))}
              onChange={(v) => setHi(Number(v))}
            />
            <p className="text-ink-800 text-sm" aria-live="polite" data-testid="tg-rate">
              {hUsable === null
                ? 'B sortirait de l’ensemble de définition : prends un autre h.'
                : frenchSpaces(
                    `h = ${n(hUsable)} : pente de (AB) = (f(a + h) − f(a))/h ${eq(rate(fn, a, hUsable))} ; f′(a) ${eq(m)}.`,
                  )}
            </p>
            <p className="text-ink-600 text-xs">
              {frenchSpaces(
                `Plus h est petit, plus B se rapproche de A et la sécante de la tangente : le taux d’accroissement tend vers f′(a).${side === -1 ? ' Ici B est pris à gauche de A (h < 0) pour rester sur la figure.' : ''}`,
              )}
            </p>
          </>
        )}
      </div>

      <section aria-label="Courbe de f′" className="space-y-2">
        <p className="text-ink-500 text-xs font-semibold">
          {frenchSpaces('Courbe de f′ : le point (a ; f′(a)) à chaque passage de A')}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <ToggleChip on={full} onClick={() => setFull(!full)}>
            Toute la courbe
          </ToggleChip>
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => {
              setVisited([a]);
              setFull(false);
            }}
          >
            Effacer
          </button>
        </div>
        <DerivPlot fn={fn} visited={visited} full={full} />
      </section>
    </div>
  );
}

function ReadChallenge({ rank, seed }: { rank: number; seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const [q, setQ] = useState<ReadQuiz>(() => makeReadQuiz(rank, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [count, setCount] = useState(1);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const frame = makeFrame(q.fn.window, SIZE);
  const fa = q.fn.f(q.a);
  const done = picked !== null && count >= ROUNDS;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;
  const others = tangentNodes(q.fn, q.a).filter(([x]) => x !== q.a);

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const pick = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    if (done) {
      setCount(1);
      setScore(0);
    } else setCount(count + 1);
    moved.current = true;
    setQ(makeReadQuiz(rank, rnd, q));
    setPicked(null);
  };
  const { vp } = frame;
  const lattice: Array<[number, number]> = [];
  for (let x = Math.ceil(vp.xMin); x <= Math.floor(vp.xMax); x++)
    for (let y = Math.ceil(vp.yMin); y <= Math.floor(vp.yMax); y++) lattice.push([x, y]);

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        Question {count} / {ROUNDS} · {score} juste{score > 1 ? 's' : ''}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(
          `Voici la courbe de f et sa tangente au point A d’abscisse a = ${xText(q.a)}. Que vaut f′(${xText(q.a)}) ?`,
        )}
      </p>
      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={`Courbe de f et tangente en A(${xText(q.a)} ; ${xText(fa)})${
            others.length
              ? `, qui passe aussi par ${others
                  .slice(0, 2)
                  .map(([x, y]) => `(${n(x)} ; ${n(y)})`)
                  .join(' et ')}`
              : ''
          }.`}
          data-testid="tg-quiz-figure"
        >
          <PlotGrid frame={frame} step={1} />
          {/* nœuds du quadrillage bien visibles : on compte les carreaux */}
          {lattice.map(([x, y]) => (
            <circle
              key={`${x},${y}`}
              cx={frame.tx(x)}
              cy={frame.ty(y)}
              r={1.6}
              fill="var(--color-ink-300)"
            />
          ))}
          <path d={curvePath(q.fn, frame)} fill="none" stroke={CURVE} strokeWidth={3} />
          <line {...lineAcross(frame, q.a, fa, q.answer)} stroke={TANGENT} strokeWidth={2.2} />
          {picked !== null && <SlopeTriangle frame={frame} fn={q.fn} a={q.a} />}
          <circle cx={frame.tx(q.a)} cy={frame.ty(fa)} r={6} fill={CURVE} />
          <HaloText
            x={frame.tx(q.a) - 14}
            y={frame.ty(fa) - 10}
            fontSize={14}
            fontWeight={700}
            fill={LABEL}
          >
            A
          </HaloText>
        </svg>
      </div>
      <div role="group" aria-label="Réponses" className="grid grid-cols-2 gap-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => pick(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      {chosen && (
        <Note live tone={chosen.correct ? 'success' : 'error'}>
          {frenchSpaces(
            chosen.correct
              ? `Exact : pour 1 vers la droite, la tangente ${q.answer > 0 ? 'monte' : q.answer < 0 ? 'descend' : 'reste à la même hauteur'}${q.answer !== 0 ? ` de ${n(Math.abs(q.answer))}` : ''}. Et f′(x) = ${q.fn.derivText}${q.fn.id === 'exp' ? ' : pour eˣ, f′ = f partout' : ''}.`
              : `${READ_HINT[chosen.mistake ?? 'near']} Ici f′(${xText(q.a)}) = ${right.text}.`,
          )}
        </Note>
      )}
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">{`Score : ${score} / ${ROUNDS}`}</p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

export function TangentTool({
  level,
  seed,
}: {
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  // niveau inconnu : tout l'outil (ln compris)
  const rank = Math.min(7, Math.max(6, schoolLevelRank(level) ?? 7));
  const [mode, setMode] = useState<'explore' | 'read'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'read', label: frenchSpaces('Lire f′(a)') },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'read'}>
        <ReadChallenge rank={rank} seed={seed} />
      </div>
    </div>
  );
}
